import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import activateImage from "assets/activate.svg";
import enhancementsImage from "assets/enhancements.svg";
import Button from "components/base/Button";
import {
  CategoryGroup,
  CategoryName,
  CategorySeparator,
  DataWrapper,
  ScreenButtonWrapper,
  screenItemMixin,
  Section,
  SectionContentScroll,
  SectionTitle,
} from "components/base/DataScreenBase";
import Medallions from "components/base/Medallions";
import SkillIcon from "components/base/SkillIcon";
import Tooltip, { TooltipDesc, TooltipName } from "components/base/Tooltip";
import { pilotScreenAnimation } from "components/styles/animations";
import { media } from "components/styles/breakpoints";
import { getPilotData } from "libs/stats";
import { buyResetSkills, buyTrainSkill } from "redux/actions";
import {
  getBuildInfo,
  getMedallions,
  getPilotSkills,
} from "redux/selectors/characterSelectors";
import { getCharacterFighterData } from "redux/selectors/fightSelectors";
import { COLORS } from "utils/constants";
import { getGroupedSkills } from "libs/skill";
import { PilotTypes } from "types";

interface PilotSkillsProps {
  atShipworks: boolean;
}

interface IconProps {
  image: string;
}

const PilotInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PilotImageWrapper = styled.div`
  display: flex;
  position: relative;
  justify-content: center;
  align-items: center;
  border: 1px #43fffa80 solid;
  border-radius: 5px;
  background-color: #000000;
  background-size: 0.3em 0.3em;
  background-position: center center;
  background-image: linear-gradient(to bottom, #aaaaaa50 1px, transparent 1px);
  animation: ${pilotScreenAnimation} 4s linear infinite;
`;

const PilotImage = styled.img`
  width: 50%;
  display: block;
  mix-blend-mode: color-dodge;
  z-index: 2;
`;

const PilotName = styled.div`
  font-size: 4vw;
  text-transform: uppercase;
  font-weight: 600;
  line-height: 1;
  position: absolute;
  mix-blend-mode: hard-light;
  color: ${COLORS.PILOTS};
  text-align: center;
`;

const PilotDesc = styled.div`
  ${screenItemMixin}
  font-size: 12px;

  ${media.desktop`
    font-size: 16px;
  `}
`;

export const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Skills = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  ${media.desktop`
    gap: 15px;
  `}
`;

const SkillInfo = styled.div`
  display: flex;
  flex-grow: 1;
  align-items: center;
  justify-content: space-between;
`;

const SkillWrapper = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const SkillIconWrapper = styled.div`
  width: 32px;
  aspect-ratio: 1/1;
  font-size: 18px;

  ${media.desktop`
    width: 44px;
    font-size: 24px;
  `}
`;

const Name = styled.div`
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;

  ${media.desktop`
    font-size: 18px;
  `}
`;

const Unlocked = styled.div`
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  color: ${COLORS.UNLOCKED};

  ${media.desktop`
    font-size: 18px;
  `}
`;

const SkillMain = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SkillDefault = styled.div`
  font-size: 20px;
  font-weight: 600;
  text-transform: uppercase;
  color: ${COLORS.PILOTS};
`;

const TrainAction = styled.div`
  display: flex;
  align-items: center;
  justify-content: end;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  text-transform: uppercase;
  color: ${COLORS.ENHANCEMENTS};

  ${media.desktop`
    font-size: 18px;
  `}
`;

const TrainIcon = styled.div`
  width: 12px;
  height: 12px;
  mask: url(${activateImage}) no-repeat center;
  background-color: white;
  aspect-ratio: 1/1;
`;

const EnhancementsIcon = styled.div<IconProps>`
  width: 12px;
  height: 12px;
  mask: url(${(props) => props.image}) no-repeat center;
  background-color: ${COLORS.ENHANCEMENTS};

  ${media.desktop`
    width: 16px;
    height: 16px;
  `}
`;

const EnhancementCost = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  color: ${COLORS.ENHANCEMENTS};
`;

export default function PilotSkills({ atShipworks = false }: PilotSkillsProps) {
  const dispatch = useDispatch();

  const characterFighter = useSelector(getCharacterFighterData);
  const { pilot: currentPilot } = useSelector(getBuildInfo);
  const skills = useSelector(getPilotSkills);
  const medallions = useSelector(getMedallions);

  const {
    name: pilotName,
    images: pilotImages,
    description: pilotDescription,
  } = getPilotData(currentPilot);

  const unlockedSkills = skills.filter((skill) => !!skill.isTrained);
  const canReset = unlockedSkills.length > 0;

  const groupedSkills = getGroupedSkills(currentPilot, skills);

  const trainSkill = (skill: string) => {
    dispatch(buyTrainSkill({ skill }));
  };

  return (
    <DataWrapper>
      <Section>
        <SectionTitle>Pilot Info</SectionTitle>
        <SectionContentScroll>
          <PilotInfo>
            <PilotImageWrapper>
              <PilotImage src={pilotImages.avatar} />
              <PilotName>{pilotName}</PilotName>
            </PilotImageWrapper>
            <PilotDesc>{pilotDescription}</PilotDesc>
          </PilotInfo>
        </SectionContentScroll>
      </Section>
      <Section>
        <SectionTitle>Skills List</SectionTitle>
        <SectionContentScroll>
          <Medallions />
          <ListWrapper>
            {Object.entries(groupedSkills).map(([pilot, groupData]) => {
              const pilotInfo = getPilotData(pilot as PilotTypes);
              return (
                <CategoryGroup key={pilot}>
                  <CategoryName>
                    {pilotInfo.name}{" "}
                    <Tooltip
                      content={
                        <>
                          <TooltipName>Pilot Skill Cost</TooltipName>
                          {currentPilot === pilot ? (
                            <TooltipDesc>
                              Given you are a {pilotInfo.name}, your pilot
                              profession's skills cost their base medallions
                              with no multiplier.
                            </TooltipDesc>
                          ) : (
                            <TooltipDesc>
                              The medallion cost of training skills of other
                              pilot professions is higher. {pilotInfo.name}{" "}
                              skills are {groupData.costMultiplier}x higher than
                              their normal base medallion cost.
                            </TooltipDesc>
                          )}
                        </>
                      }
                      color={COLORS.ENHANCEMENTS}
                    >
                      <EnhancementCost>
                        <EnhancementsIcon image={enhancementsImage} />
                        {groupData.costMultiplier}x
                      </EnhancementCost>
                    </Tooltip>
                    <CategorySeparator />
                  </CategoryName>
                  <Skills>
                    {groupData.skills.map((skill) => {
                      const canTrainSkill =
                        skill.medallions <= medallions.available;
                      return (
                        <SkillWrapper key={skill.slug}>
                          <SkillIconWrapper>
                            <SkillIcon
                              key={skill.slug}
                              skill={skill}
                              attacker={characterFighter}
                              isShowValues
                            />
                          </SkillIconWrapper>
                          <SkillInfo>
                            <SkillMain>
                              <Name>{skill.name}</Name>
                              <EnhancementCost>
                                <EnhancementsIcon image={enhancementsImage} />
                                {skill.medallions}
                              </EnhancementCost>
                              {skill.isDefault && (
                                <Tooltip
                                  content={
                                    <>
                                      <TooltipName>Default Skill</TooltipName>
                                      <TooltipDesc>
                                        {skill.name} is the default skill for
                                        your pilot profession {pilotInfo.name}.
                                      </TooltipDesc>
                                      <TooltipDesc>
                                        Default skills are always available and
                                        cannot be untrained.
                                      </TooltipDesc>
                                    </>
                                  }
                                  color={COLORS.PILOTS}
                                >
                                  <SkillDefault>✧</SkillDefault>
                                </Tooltip>
                              )}
                            </SkillMain>
                            {skill.isTrained ? (
                              <Unlocked>Unlocked</Unlocked>
                            ) : (
                              <TrainAction>
                                {!!atShipworks && (
                                  <Tooltip
                                    isDisabled={!canTrainSkill}
                                    content={
                                      <>
                                        <TooltipName>
                                          Train {skill.name}
                                        </TooltipName>
                                        <TooltipDesc>
                                          Train {skill.name} for{" "}
                                          {skill.medallions} medallions.
                                        </TooltipDesc>
                                      </>
                                    }
                                  >
                                    <Button
                                      $style="normal"
                                      $size="xsmall"
                                      $disabled={!canTrainSkill}
                                      onClick={() => trainSkill(skill.slug)}
                                    >
                                      <TrainIcon />
                                    </Button>
                                  </Tooltip>
                                )}
                              </TrainAction>
                            )}
                          </SkillInfo>
                        </SkillWrapper>
                      );
                    })}
                  </Skills>
                </CategoryGroup>
              );
            })}
          </ListWrapper>
          {!!atShipworks && (
            <ScreenButtonWrapper>
              <Tooltip
                color={COLORS.IMPORTANT}
                content={
                  <>
                    <TooltipName>Reset Skills</TooltipName>
                    <TooltipDesc>
                      Reset your trained skills and get back all your spent
                      skill medallions.
                    </TooltipDesc>
                  </>
                }
              >
                <Button
                  $style="important"
                  $size="medium"
                  $disabled={!canReset}
                  onClick={() => dispatch(buyResetSkills())}
                >
                  Reset Skills
                </Button>
              </Tooltip>
            </ScreenButtonWrapper>
          )}
        </SectionContentScroll>
      </Section>
    </DataWrapper>
  );
}
